import { useState } from 'react';
import { Button, Input } from '@staff-app/ui';
import type { IntlRequest } from '@staff-app/shared-types';
import { countryName, useUpdateIntlRequest } from '../hooks/useIntlRequests';

/**
 * The ship-to fields, in the order the intake form asks them — so the two surfaces read the same
 * and nobody has to re-find a field they filled in an hour ago.
 *
 * `required` is the phone alone (requester, 2026-09-09), and the server enforces it on a
 * correction as well as at filing: an international courier cannot book a delivery without one,
 * so a cleared box would quietly make a complete request unshippable.
 */
const FIELDS: ReadonlyArray<{
  key: keyof IntlRequest & string;
  label: string;
  required?: boolean;
  wide?: boolean;
}> = [
  { key: 'shipToName', label: 'Recipient name', wide: true },
  { key: 'shipToLine1', label: 'Address', wide: true },
  { key: 'shipToLine2', label: 'Address 2', wide: true },
  { key: 'shipToCity', label: 'City' },
  { key: 'shipToRegion', label: 'Region / state' },
  { key: 'shipToPostalCode', label: 'Postal code' },
  { key: 'shipToPhone', label: 'Phone', required: true },
];

/**
 * Is this request's destination still correctable?
 *
 * A MIRROR of `IntlRequestsService._assertDestinationOpen`, and the server is the authority — this
 * only decides whether to OFFER the edit. Duplicating the rule is the lesser evil: the
 * alternative is a form that submits into a refusal, which teaches an operator that the button
 * lies.
 *
 * THE WINDOW CLOSES WHEN AMAZON HAS THE ORDER, not when the invoice is paid. That was the
 * original line and IR-12 is why it moved: payment routinely lands before somebody fills in a
 * missing phone number, so freezing at paid left a request that could be neither corrected nor
 * fulfilled. The address has to be right at SUBMISSION time, and the submission is what carries
 * it to Amazon — after which Amazon holds its own copy and ours has stopped being the one that
 * matters.
 *
 * A CANCELLED ORDER RE-OPENS IT, which is exactly how a wrong address gets fixed: cancel,
 * correct, submit again.
 */
export function canEditShipTo(r: IntlRequest): boolean {
  return !r.mcfSubmittedAt || Boolean(r.mcfCancelledAt);
}

/** Why it is closed, in the words the panel shows. Only called when `canEditShipTo` is false. */
export function shipToFrozenReason(r: IntlRequest): string {
  if (r.mcfReleasedAt) {
    return (
      `Amazon order ${r.mcfOrderId} has been released to ship, so the goods are on their way to ` +
      'this address. If it is wrong, change the destination with the carrier and add a note here.'
    );
  }
  return (
    `Amazon holds fulfilment order ${r.mcfOrderId} with its own copy of this address. To change ` +
    'it: cancel the fulfilment order, correct it here, and submit again.'
  );
}

/**
 * Correct one request's shipping address — the one edit this surface allows after filing.
 *
 * WHY ONLY SHIP-TO (requester, 2026-09-09: "before the invoice is paid, we still have the ability
 * to update the shipping address. only the ship to info"). It is the field a rep filing from a
 * call most often does not have, and the one the customer supplies later. Everything else on the
 * record either came from Stripe (the customer, the VAT number) or is the request's identity.
 *
 * SHIP-TO COUNTRY IS NOT EDITABLE and is shown read-only with the reason. It is not in the
 * mutation's input at all: together with the device model it decides tax treatment, legality,
 * which Amazon marketplace can reach the address and what freight costs, so a different country
 * is a different request rather than a typo. Saying so beside the field is what stops the next
 * person filing a ticket asking for it.
 *
 * ONLY WHAT CHANGED IS SENT. An unchanged field is omitted rather than re-sent, so
 * `last_updated_by` / `last_updated_at` mean "somebody changed something" — and pressing Save on
 * an untouched form cannot stamp a correction that corrected nothing. It is also why Save is
 * disabled until something differs.
 */
export function IntlShipToEditor({
  request,
  onDone,
}: {
  request: IntlRequest;
  onDone: () => void;
}) {
  // Initialised from the row; `?? ''` because an input's value may not be null. The component is
  // mounted with `key={request.id}` by the panel, so switching rows starts a fresh form rather
  // than carrying one request's typing onto another.
  const [form, setForm] = useState<Record<string, string>>(() =>
    Object.fromEntries(FIELDS.map((f) => [f.key, (request[f.key] as string | null) ?? ''])),
  );
  const [update, { loading, error }] = useUpdateIntlRequest();

  const set = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }));

  // An empty string is a real instruction here — it CLEARS an optional line (a suite number
  // entered by mistake has to be removable), which is why the diff compares trimmed strings
  // rather than skipping blanks.
  const changed = FIELDS.filter(
    (f) => form[f.key].trim() !== (((request[f.key] as string | null) ?? '').trim() || ''),
  );
  const phoneEmpty = !form.shipToPhone.trim();

  const save = async () => {
    if (!changed.length || phoneEmpty) return;
    try {
      await update({
        variables: {
          input: {
            requestId: request.id,
            ...Object.fromEntries(changed.map((f) => [f.key, form[f.key].trim()])),
          },
        },
      });
      onDone();
    } catch {
      // Left open, with the typing intact, and the server's words rendered below — a refusal
      // (paid in the meantime, a blanked phone) is information, not a reason to lose the edit.
    }
  };

  return (
    <div className="sm:col-span-2" data-testid="intl-shipto-editor">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {FIELDS.map((f) => (
          <div key={f.key} className={f.wide ? 'sm:col-span-2' : undefined}>
            <label
              className="text-xs uppercase tracking-wide text-muted-foreground"
              htmlFor={`intl-shipto-${f.key}`}
            >
              {f.label}
              {f.required ? <span className="ml-0.5 text-destructive">{'*'}</span> : null}
            </label>
            <Input
              id={`intl-shipto-${f.key}`}
              className="mt-1"
              value={form[f.key]}
              onChange={(e) => set(f.key, e.target.value)}
              aria-invalid={f.required && !form[f.key].trim() ? true : undefined}
              data-testid={`intl-shipto-input-${f.key}`}
            />
          </div>
        ))}

        {/* Read-only, with the reason. See the docblock — this is the field somebody would
            otherwise raise a ticket about. */}
        <div className="sm:col-span-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{'Country'}</p>
          <p className="mt-1 text-sm">{`${countryName(request.shipToCountry)} (${request.shipToCountry})`}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {'The country cannot be changed — it decides the tax treatment, the freight and which '}
            {'Amazon marketplace can reach the address, so a different country is a different '}
            {'request. File a new one.'}
          </p>
        </div>
      </div>

      {/* Saving here does not only correct the row. A ship-to correction re-pushes the address
          onto the Stripe customer's BILLING address (requester, 2026-09-18), overwriting what is
          there — so this surface has to say so too, or a rep fixing a typo silently rewrites a
          customer record. Stated rather than shown: unlike the intake form, this editor does not
          resolve the Stripe customer, and adding a lookup to name the address would be a Stripe
          read on every drawer open. */}
      <p className="mt-3 text-xs text-muted-foreground" data-testid="intl-shipto-stripe-note">
        {'Saving also replaces this customer’s billing address in Stripe with the address above.'}
      </p>

      {phoneEmpty ? (
        <p className="mt-3 text-xs text-destructive" data-testid="intl-shipto-phone-required">
          {'A phone number is required — an international courier cannot book a delivery without '}
          {'one.'}
        </p>
      ) : null}

      {error ? (
        // Verbatim: the server names the request and what to do instead, which is the recovery
        // information rather than decoration.
        <p className="mt-3 text-sm text-destructive" data-testid="intl-shipto-error">
          {error.message}
        </p>
      ) : null}

      <div className="mt-4 flex items-center gap-2">
        <Button
          size="sm"
          onClick={save}
          disabled={loading || !changed.length || phoneEmpty}
          data-testid="intl-shipto-save"
        >
          {loading ? 'Saving…' : 'Save address'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone} data-testid="intl-shipto-cancel">
          {'Cancel'}
        </Button>
        {changed.length ? (
          // Says WHAT will be written, because the diff is the thing being agreed to — and it is
          // the only signal that an empty box means "clear this".
          <span className="text-xs text-muted-foreground" data-testid="intl-shipto-changed">
            {`${changed.map((f) => f.label.toLowerCase()).join(', ')} will change`}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{'Nothing changed yet'}</span>
        )}
      </div>
    </div>
  );
}
